/**
 * Privacy-preserving analytics client (REN-18).
 *
 * We fire three named events at a same-origin path — `/beacon/visit`,
 * `/beacon/complete`, `/beacon/share` — served by a no-op Cloudflare Pages
 * Function that responds 204. Cloudflare's built-in traffic analytics groups
 * requests by URL so the CEO can read visit / completion / share counts
 * straight from the Pages dashboard without any storage on our side.
 *
 * REN-83: the page being measured is appended to that path, so a hit reads
 * `/beacon/visit/learn/rent-vs-buy-chicago/` rather than a bare
 * `/beacon/visit` that puts nine metro pages in one bucket. The page goes in
 * the *path*, not a query string, because only the path is groupable: on
 * this zone's plan `httpRequestsAdaptiveGroups` serves `clientRequestPath`
 * but rejects `clientRequestQuery` with an authz error, and Pages traffic
 * analytics discards query strings when it aggregates "top requests". A
 * dimension we cannot group by is the same as no dimension. Rolling every
 * page back up is a prefix filter — `clientRequestPath_like: "/beacon/visit%"`.
 *
 * The transport is deliberately dumb: no request body, no cookies (this
 * origin sets none anyway), no user identifier, no persistent state, and no
 * referrer capture — the only thing we read is our own `location.pathname`,
 * normalised through `pageDimension()` below. Global Privacy Control and Do
 * Not Track are honoured — if either is asserted we emit nothing.
 *
 * The `visit` event is idempotent per page load; `complete` is idempotent
 * per session. `share` fires every time the user asks for a share link, so
 * repeated shares are countable but still cannot be tied to a person.
 */

type AnalyticsEvent = "visit" | "complete" | "share";

interface AnalyticsWindow {
  readonly navigator?: {
    doNotTrack?: string | null;
    globalPrivacyControl?: boolean;
    sendBeacon?: (url: string, data?: BodyInit | null) => boolean;
  };
  readonly location?: { pathname?: string };
  fetch?: typeof fetch;
}

/**
 * Longest page dimension we will emit. Our deepest real URL is a metro
 * article slug (~40 chars); the cap exists so a crafted or junk URL cannot
 * blow up the cardinality of the analytics grouping with one giant row.
 */
const MAX_PAGE_LENGTH = 96;

/** Deepest real page is `/learn/<slug>/` — two segments. Four is slack. */
const MAX_PAGE_SEGMENTS = 4;

const firedOnce = new Set<AnalyticsEvent>();

/**
 * Fire a single analytics event. Safe to call from any environment: no-ops
 * when window/navigator are unavailable (SSR, tests) or the user has opted
 * out via DNT / GPC. `once: true` (default for `visit` and `complete`)
 * suppresses duplicates within the current page load.
 */
export function trackEvent(
  event: AnalyticsEvent,
  options: { once?: boolean; win?: AnalyticsWindow } = {},
): boolean {
  const win = options.win ?? (typeof window !== "undefined" ? (window as unknown as AnalyticsWindow) : undefined);
  if (!win) return false;
  if (isOptedOut(win)) return false;
  const once = options.once ?? event !== "share";
  if (once && firedOnce.has(event)) return false;

  const path = `/beacon/${event}${pageDimension(win.location?.pathname)}`;
  const sent = sendBeacon(win, path);
  if (sent && once) firedOnce.add(event);
  return sent;
}

/**
 * Normalise `location.pathname` into the suffix we append to the beacon
 * path. The output is always either "" (nothing usable) or a string
 * starting with "/", so `/beacon/visit` + suffix is a well-formed path.
 *
 * Normalisation is deliberately lossy and closed: we lowercase, keep only
 * `[a-z0-9-_./]`, collapse repeated slashes, drop anything past the segment
 * or length cap, and never look at the query string, the hash, the host or
 * the referrer. Every real URL on this site survives it unchanged; anything
 * else degrades to a shorter path rather than becoming a unique identifier.
 */
export function pageDimension(pathname: string | undefined): string {
  if (!pathname || pathname[0] !== "/") return "";
  // `location.pathname` already excludes the query and hash. Cut at them
  // anyway: stripping `?` and `&` character-wise would *concatenate* the
  // scenario values into the path rather than drop them, which is exactly
  // the leak we must not have. Truncating is the safe failure mode.
  const cut = pathname.search(/[?#]/);
  const bare = cut === -1 ? pathname : pathname.slice(0, cut);
  const trailingSlash = bare.length > 1 && bare.endsWith("/");
  const segments = bare
    .toLowerCase()
    .split("/")
    .map((segment) => segment.replace(/[^a-z0-9\-_.]/g, ""))
    .filter((segment) => segment.length > 0)
    .slice(0, MAX_PAGE_SEGMENTS);

  // `/` normalises to `/` so the home page is its own row rather than
  // sharing the bare `/beacon/visit` path with "we could not tell".
  if (segments.length === 0) return "/";

  const path = "/" + segments.join("/") + (trailingSlash ? "/" : "");
  return path.length > MAX_PAGE_LENGTH ? "" : path;
}

/**
 * True when the user has asserted either Global Privacy Control or the
 * legacy Do Not Track header. We treat either as a hard opt-out — the
 * event never leaves the browser.
 */
function isOptedOut(win: AnalyticsWindow): boolean {
  const nav = win.navigator;
  if (!nav) return false;
  if (nav.globalPrivacyControl === true) return true;
  if (nav.doNotTrack === "1") return true;
  return false;
}

function sendBeacon(win: AnalyticsWindow, path: string): boolean {
  const nav = win.navigator;
  if (nav?.sendBeacon) {
    try {
      // Empty body — the URL alone carries the event name.
      return nav.sendBeacon(path);
    } catch {
      // Fall through to fetch.
    }
  }
  if (win.fetch) {
    try {
      void win.fetch(path, {
        method: "POST",
        keepalive: true,
        credentials: "omit",
        mode: "same-origin",
      });
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Test-only: reset the "fired once" cache between test cases.
 */
export function __resetAnalyticsForTests(): void {
  firedOnce.clear();
}
